/* Gallery strip: continuous auto-scroll plus grab-and-pull.

   Both run on scrollLeft rather than a transform, so dragging and the
   automatic drift share one value and can never fight each other — the drag
   just takes over where the animation left off. Looping works because the
   markup holds the photo list twice: once scrollLeft passes half the track,
   subtracting that half lands on an identical frame, so there is no jump. */
class LandingGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.speed = parseFloat(this.dataset.speed || '40'); // px per second
    this.autoplay = this.dataset.autoplay === 'true';
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dragging = false;
    this.hovering = false;
    this.moved = 0;
    this.lastFrame = null;

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.tick = this.tick.bind(this);

    this.track.addEventListener('pointerdown', this.onDown);
    this.track.addEventListener('click', (e) => {
      if (this.moved > 6) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    this.addEventListener('mouseenter', () => { this.hovering = true; });
    this.addEventListener('mouseleave', () => { this.hovering = false; });

    // A wheel or touch scroll should not be dragged back by the animation.
    this.track.addEventListener('scroll', () => { this.wrapAround(); }, { passive: true });

    if (this.autoplay && !this.reduced) requestAnimationFrame(this.tick);
  }

  /* Keeps scrollLeft inside the first copy of the list. */
  wrapAround() {
    const half = this.track.scrollWidth / 2;
    if (half <= 0) return;
    if (this.track.scrollLeft >= half) this.track.scrollLeft -= half;
    else if (this.track.scrollLeft < 0) this.track.scrollLeft += half;
  }

  tick(now) {
    if (this.lastFrame === null) this.lastFrame = now;
    const delta = Math.min((now - this.lastFrame) / 1000, 0.05);
    this.lastFrame = now;

    if (!this.dragging && !this.hovering) {
      this.track.scrollLeft += this.speed * delta;
      this.wrapAround();
    }
    requestAnimationFrame(this.tick);
  }

  onDown(e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    this.dragging = true;
    this.moved = 0;
    this.startX = e.clientX;
    this.startScroll = this.track.scrollLeft;
    this.track.setPointerCapture(e.pointerId);
    this.classList.add('is-dragging');
    this.track.addEventListener('pointermove', this.onMove);
    this.track.addEventListener('pointerup', this.onUp);
    this.track.addEventListener('pointercancel', this.onUp);
  }

  onMove(e) {
    if (!this.dragging) return;
    const delta = e.clientX - this.startX;
    this.moved = Math.max(this.moved, Math.abs(delta));
    this.track.scrollLeft = this.startScroll - delta;
    this.wrapAround();
  }

  onUp(e) {
    this.dragging = false;
    this.classList.remove('is-dragging');
    this.track.removeEventListener('pointermove', this.onMove);
    this.track.removeEventListener('pointerup', this.onUp);
    this.track.removeEventListener('pointercancel', this.onUp);
    if (this.track.hasPointerCapture && this.track.hasPointerCapture(e.pointerId)) {
      this.track.releasePointerCapture(e.pointerId);
    }
  }
}

if (!customElements.get('landing-gallery')) {
  customElements.define('landing-gallery', LandingGallery);
}
